import React, { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import { truncateReportedError } from "./appPreviewPayloadUtils";

/**
 * AppPreviewIframe component encapsulates the iframe preview logic,
 * including URL generation, loading state management, and error reporting.
 */
export default function AppPreviewIframe({
    stepId,
    currentVersion,
    componentServerUrl,
    useCustomDomain,
    selectedPageId,
    components,
    selectedEnvironment,
    handleErrorLog,
    addConsoleLogToStepExecution,
    addAxiosResponseToStepExecution,
    onAiSuggestion,
    onHumanSuggestion,
    updateCurrentPageContext,
    t,
    isMobilePreview,
    reloadTrigger, // Prop to trigger reload from parent
    isSelectMode,
    onElementSelected,
    viewAsUserToken, // Token for admin impersonation preview
    previewToken, // Short-lived workflow-scoped JWT (replaces x_token_auth)
    onPageChanged, // Callback when iframe navigation matches a known page
}) {
    const iframeRef = useRef(null);
    const errorReportedRef = useRef(new Set());
    const [isIframeLoading, setIsIframeLoading] = useState(true);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [showProgressBar, setShowProgressBar] = useState(true);
    const [retryCount, setRetryCount] = useState(0);
    const retryTimeoutRef = useRef(null);
    const selectModeTimeoutRef = useRef(null);
    const loadWatchdogRef = useRef(null);
    // Captured once on mount so a falsy currentVersion doesn't mint a new
    // URL every render and cancel the in-flight iframe load.
    const stableVersionFallbackRef = useRef(Date.now());
    // Last time the iframe sent us a postMessage — proof of life used by
    // the watchdog to distinguish "stuck" from "just slow".
    const lastIframeActivityRef = useRef(0);
    // AbortController for the diagnostic probe fetch in handleIframeLoad,
    // so a slow connection can't leave the probe hanging indefinitely.
    const probeAbortRef = useRef(null);
    // When we want to retry but can't (offline / link interrupted mid-flight),
    // park the intent here so the next 'online' event can resume it. Cleared
    // when a real retry is scheduled or the iframe finishes loading.
    const pendingRetryRef = useRef(null);
    // Timestamp at which the network went offline. The 'online' handler
    // reads this to (a) gate brief flap-recovery and (b) decide whether to
    // force a refresh even when the iframe had already loaded.
    const offlineSinceRef = useRef(null);
    // Mirror of isIframeLoading + retryCount + loadFailed accessible from the
    // network event listeners (which capture closures with `[]` deps).
    const isIframeLoadingRef = useRef(true);
    const retryCountRef = useRef(0);
    const loadFailedRef = useRef(false);

    const [isOffline, setIsOffline] = useState(
        typeof navigator !== "undefined" ? !navigator.onLine : false
    );
    const [loadFailed, setLoadFailed] = useState(false);

    // Full-jitter exponential backoff (AWS Builder's Library pattern).
    // Prevents thundering-herd when a deploy 504s many sessions at once —
    // each client retries at a random point within the growing window.
    const backoffWithJitter = (attempt) => {
        const base = 500;
        const cap = 8000;
        const exp = Math.min(cap, base * Math.pow(2, attempt));
        return Math.random() * exp;
    };

    // Unified error reporting function that prevents duplicates
    const reportErrorOnce = useCallback(
        (errorMessage) => {
            if (!errorMessage || !handleErrorLog) return;

            const normalizedMessage = truncateReportedError(errorMessage);
            if (!normalizedMessage) return;

            if (errorReportedRef.current.has(normalizedMessage)) {
                return;
            }

            errorReportedRef.current.add(normalizedMessage);
            handleErrorLog(normalizedMessage);

            setTimeout(() => {
                errorReportedRef.current.delete(normalizedMessage);
            }, 10000);
        },
        [handleErrorLog]
    );

    // Helper function to get iframe URL
    const getIframeUrl = useCallback(() => {
        if (!stepId) return "";
        const basePath = useCustomDomain
            ? componentServerUrl
            : `${componentServerUrl}/apps/${stepId}`;

        let route = "";
        if (selectedPageId && components) {
            const page = components.find((c) => c.id === selectedPageId);
            if (page && page.filePath) {
                let path = page.filePath.replace(/^pages\//, "");
                path = path.replace(/\.[^/.]+$/, "");
                if (!path.startsWith("/")) {
                    path = "/" + path;
                }
                route = path.toLowerCase();
            }
        }

        const queryParams = new URLSearchParams({
            v: String(currentVersion || stableVersionFallbackRef.current),
            debug: "true",
            isInFrame: "true",
            _cb: String(reloadTrigger || 0),
        });

        // Auth: viewAsUser impersonation token (admin) takes priority,
        // otherwise the short-lived workflow-scoped preview token. We no
        // longer pass the long-lived platform Bearer in the URL.
        if (viewAsUserToken) {
            queryParams.set("x_deep_link_token", viewAsUserToken);
        } else if (previewToken) {
            queryParams.set("x_deep_link_token", previewToken);
        }

        if (!useCustomDomain && selectedEnvironment) {
            queryParams.set("environment", selectedEnvironment);
        }

        const target = `${basePath}${route}?${queryParams.toString()}`;

        // On retry attempt 2, route the iframe through /__bust on the
        // component-server origin so the browser clears stickiness cookies
        // (via Clear-Site-Data) before remounting. This is the only way to
        // escape a dead sticky target — the parent can't touch the iframe's
        // origin cookies directly.
        if (retryCount === 2) {
            const origin = new URL(componentServerUrl);
            const next = target.startsWith(origin.origin)
                ? target.slice(origin.origin.length)
                : target;
            return `${origin.origin}/__bust?next=${encodeURIComponent(next)}`;
        }

        return target;
    }, [
        stepId,
        useCustomDomain,
        componentServerUrl,
        selectedPageId,
        components,
        currentVersion,
        selectedEnvironment,
        reloadTrigger,
        viewAsUserToken,
        previewToken,
        retryCount,
    ]);

    // Schedule a retry via the key-based remount. Covers three failure modes:
    //   1. iframe.onerror fires (network-level failure: connection reset, DNS
    //      miss, TCP timeout — handleIframeLoad never runs)
    //   2. load watchdog times out with no proof-of-life from the iframe
    //   3. ALB gateway 5xx returned by the cookie-less probe in handleIframeLoad
    // The /__bust hop on attempt 2 is handled declaratively inside getIframeUrl
    // so React's key-based remount doesn't race the imperative src setter.
    const scheduleRetry = useCallback(
        (reason) => {
            // Don't burn the retry budget while the network is known down.
            // Park the intent so the next 'online' event can resume it.
            if (typeof navigator !== "undefined" && navigator.onLine === false) {
                console.warn(
                    `[AppPreviewIframe] Deferring retry while offline — ${reason}`
                );
                pendingRetryRef.current = { reason, at: Date.now() };
                return false;
            }
            if (retryCount >= 3) return false;
            const newRetryCount = retryCount + 1;
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
            }
            const delay = backoffWithJitter(newRetryCount);
            // We're scheduling a real retry — clear any deferred intent.
            pendingRetryRef.current = null;
            retryTimeoutRef.current = setTimeout(() => {
                console.warn(
                    `[AppPreviewIframe] Retry ${newRetryCount}/3 — ${reason} (delay ${Math.round(delay)}ms)`
                );
                setRetryCount(newRetryCount);
            }, delay);
            return true;
        },
        [retryCount]
    );

    // Send select mode state to iframe
    const sendSelectMode = useCallback((enabled) => {
        if (!iframeRef.current?.contentWindow) return;
        const msg = enabled
            ? { type: "ENABLE_SELECT_MODE" }
            : { type: "DISABLE_SELECT_MODE" };
        iframeRef.current.contentWindow.postMessage(msg, "*");
    }, []);

    // Check if iframe loaded successfully and detect server errors
    const handleIframeLoad = useCallback(async () => {
        if (!iframeRef.current || !stepId) return;

        // Re-send select mode state after iframe loads (with delay for scripts to initialize)
        // Clear any previous timeout to prevent stale state
        if (selectModeTimeoutRef.current) {
            clearTimeout(selectModeTimeoutRef.current);
            selectModeTimeoutRef.current = null;
        }
        if (isSelectMode) {
            selectModeTimeoutRef.current = setTimeout(() => {
                sendSelectMode(true);
                selectModeTimeoutRef.current = null;
            }, 300);
        }

        try {
            const iframeWindow = iframeRef.current.contentWindow;

            try {
                const doc = iframeWindow?.document;
                if (doc && doc.body) {
                    const bodyText = doc.body.innerText || doc.body.textContent;
                    if (
                        bodyText.trim().startsWith("{") &&
                        bodyText.includes('"error"')
                    ) {
                        try {
                            const errorData = JSON.parse(bodyText);
                            if (errorData.error) {
                                reportErrorOnce(
                                    errorData.message || errorData.error
                                );
                            }
                        } catch (e) {
                            // Not JSON
                        }
                    }
                }
                setLoadingProgress(100);
                setLoadFailed(false);
                // Note: retryCount is intentionally NOT reset here. Resetting
                // it would change the iframe `key` and trigger a SECOND
                // remount (cancel-and-reload) right after a successful load.
                // The budget resets on real navigation (URL change effect)
                // and on reloadTrigger.
            } catch (crossOriginError) {
                // Cross-origin throw is the *expected* path for normal loads.
                // The probe below is purely diagnostic — its failure must NOT
                // trigger a retry, because the iframe already fired onload.
                const iframeUrl = useCustomDomain
                    ? componentServerUrl
                    : `${componentServerUrl}/apps/${stepId}`;

                if (probeAbortRef.current) {
                    probeAbortRef.current.abort();
                }
                const controller = new AbortController();
                probeAbortRef.current = controller;
                const probeTimeout = setTimeout(() => controller.abort(), 10000);

                try {
                    const response = await fetch(iframeUrl, {
                        method: "GET",
                        headers: { Accept: "application/json, text/html" },
                        signal: controller.signal,
                    });
                    clearTimeout(probeTimeout);

                    if (!response.ok) {
                        // ALB gateway errors (502/503/504/524) usually mean the
                        // sticky target was replaced mid-session by an ECS deploy.
                        // Retry via remount — getIframeUrl will route through
                        // /__bust on attempt 2 to clear the cookie cross-subdomain.
                        const isGatewayError =
                            response.status === 502 ||
                            response.status === 503 ||
                            response.status === 504 ||
                            response.status === 524;

                        if (
                            isGatewayError &&
                            scheduleRetry(`gateway ${response.status}`)
                        ) {
                            setLoadingProgress(100);
                            return;
                        }

                        const contentType =
                            response.headers.get("content-type");
                        if (
                            contentType &&
                            contentType.includes("application/json")
                        ) {
                            const errorData = await response.json();
                            reportErrorOnce(
                                errorData.message ||
                                    errorData.error ||
                                    "Failed to load app"
                            );
                        } else {
                            const errorText = await response.text();
                            if (errorText && !errorText.includes("<!DOCTYPE")) {
                                reportErrorOnce(errorText);
                            }
                        }
                    } else {
                        setLoadFailed(false);
                        // retryCount intentionally not reset here — see
                        // comment in same-origin success path above.
                    }
                    setLoadingProgress(100);
                } catch (fetchError) {
                    clearTimeout(probeTimeout);
                    // The probe failed, but the iframe's own onload already
                    // fired — that's the authoritative signal. Treat probe
                    // failure as inconclusive: log it and finish loading.
                    // Slow networks regularly time out the probe while the
                    // iframe itself is rendering fine.
                    if (fetchError.name === "AbortError") {
                        console.warn(
                            "[AppPreviewIframe] Diagnostic probe aborted (timeout or supersede)"
                        );
                    } else {
                        console.warn(
                            "[AppPreviewIframe] Diagnostic probe failed (iframe likely loaded fine):",
                            fetchError.message
                        );
                    }
                    setLoadingProgress(100);
                } finally {
                    if (probeAbortRef.current === controller) {
                        probeAbortRef.current = null;
                    }
                }
            }
        } catch (error) {
            console.error("Error checking iframe load:", error);
            setLoadingProgress(100);
        }
    }, [
        stepId,
        reportErrorOnce,
        componentServerUrl,
        useCustomDomain,
        scheduleRetry,
        isSelectMode,
        sendSelectMode,
    ]);

    // Handle iframe load errors (fires on network-level failures: connection
    // reset, DNS failure, TCP timeout, etc. — when the TCP connection never
    // opens and handleIframeLoad never runs).
    const handleIframeError = useCallback(() => {
        if (scheduleRetry("iframe.onerror")) {
            return;
        }
        setLoadingProgress(100);
        setLoadFailed(true);
        const errorMessage =
            t("appPreview.iframeLoadError") || "Failed to load app preview.";
        toast.error(t("appPreview.loadError") || "Load Error", {
            description: errorMessage,
            duration: 5000,
        });
    }, [t, scheduleRetry]);

    // Toggle select mode in iframe when state changes
    useEffect(() => {
        // Clear any pending timeout when select mode changes to prevent desync
        if (selectModeTimeoutRef.current) {
            clearTimeout(selectModeTimeoutRef.current);
            selectModeTimeoutRef.current = null;
        }
        sendSelectMode(isSelectMode);
    }, [isSelectMode, sendSelectMode]);

    // Handle messages from iframe
    useEffect(() => {
        const handleMessage = (event) => {
            const componentServerOrigin = new URL(componentServerUrl);
            if (event.origin !== componentServerOrigin.origin) return;

            // Proof of life — any message from the iframe origin means it's
            // alive. The watchdog uses this to avoid retrying a slow-but-OK
            // load, which would cancel the in-flight request.
            lastIframeActivityRef.current = Date.now();

            // Handle element selection from visual editor
            if (event.data?.type === "ELEMENT_SELECTED" && onElementSelected) {
                onElementSelected(event.data.element);
                return;
            }

            // Debug: log all console-related messages from iframe
            if (event.data?.type?.startsWith("CONSOLE")) {
                console.debug("[AppPreviewIframe] Received:", event.data?.type, event.data?.message?.substring?.(0, 100));
            }

            if (event.data?.type === "ERROR") {
                reportErrorOnce(event.data.message);
            }

            if (event.data?.type === "CONSOLE_LOG") {
                addConsoleLogToStepExecution(
                    "console.log",
                    event.data.message,
                    event.data.timestamp
                );
            }

            if (event.data?.type === "CONSOLE_INFO") {
                addConsoleLogToStepExecution(
                    "console.info",
                    event.data.message,
                    event.data.timestamp
                );
            }

            if (event.data?.type === "CONSOLE_WARN") {
                addConsoleLogToStepExecution(
                    "console.warn",
                    event.data.message,
                    event.data.timestamp
                );
            }

            if (event.data?.type === "CONSOLE_ERROR") {
                addConsoleLogToStepExecution(
                    "console.error",
                    event.data.message,
                    event.data.timestamp
                );
            }

            if (event.data?.type === "CONSOLE_AI") {
                addConsoleLogToStepExecution(
                    "console.ai",
                    event.data.message,
                    event.data.timestamp
                );
                if (onAiSuggestion) {
                    onAiSuggestion(event.data.message, event.data.metadata);
                }
            }

            if (event.data?.type === "CONSOLE_HUMAN") {
                addConsoleLogToStepExecution(
                    "console.human",
                    event.data.message,
                    event.data.timestamp
                );
                if (onHumanSuggestion) {
                    onHumanSuggestion(event.data.message, { ...event.data.metadata, data: event.data.data });
                }
            }

            if (event.data?.type === "AXIOS_SUCCESS") {
                addAxiosResponseToStepExecution(
                    {
                        request: event.data.request,
                        response: event.data.response,
                    },
                    event.data.timestamp
                );
            }

            if (event.data?.type === "AXIOS_ERROR") {
                addAxiosResponseToStepExecution(
                    {
                        request: event.data.request,
                        error: event.data.error,
                    },
                    event.data.timestamp
                );
            }

            if (event.data?.type === "SERVER_ERROR") {
                reportErrorOnce(event.data.message);
            }

            if (
                event.data?.type === "ROUTE_CHANGE" &&
                updateCurrentPageContext
            ) {
                const { route, pageName } = event.data;

                // Extract page name from route: /admin/users -> users, /dashboard -> dashboard
                const extractPageNameFromRoute = (routePath) => {
                    if (!routePath) return null;
                    // Remove leading slash and get the last segment
                    const segments = routePath.replace(/^\//, '').split('/').filter(Boolean);
                    return segments.length > 0 ? segments[segments.length - 1] : null;
                };

                // Try to find matching page - first by pageName, then by route segments
                let matchingPage = null;
                if (components) {
                    const routePageName = extractPageNameFromRoute(route);
                    const searchNames = [
                        pageName,
                        routePageName,
                        route?.replace(/^\//, ''), // Full path without leading slash
                    ].filter(Boolean);

                    for (const searchName of searchNames) {
                        if (matchingPage) break;
                        matchingPage = components.find(
                            (c) =>
                                c.type === 'PAGE' && (
                                    c.name?.toLowerCase() === searchName.toLowerCase() ||
                                    c.filePath?.toLowerCase().includes(searchName.toLowerCase())
                                )
                        );
                    }
                }

                // For fallback page name, use the last route segment (most specific)
                const fallbackPageName = pageName || extractPageNameFromRoute(route);

                updateCurrentPageContext({
                    selectedPageId: matchingPage?.id || null,
                    selectedPageName: matchingPage?.name || fallbackPageName || null,
                    selectedPageFilePath: matchingPage?.filePath || null,
                    currentRoute: route,
                    appViewMode: "preview",
                });

                // Notify parent of current page for display purposes
                if (onPageChanged) {
                    onPageChanged(matchingPage?.id || "/");
                }
            }
        };

        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [
        componentServerUrl,
        reportErrorOnce,
        addConsoleLogToStepExecution,
        addAxiosResponseToStepExecution,
        onAiSuggestion,
        onHumanSuggestion,
        updateCurrentPageContext,
        onElementSelected,
        onPageChanged,
        components,
    ]);

    // When retryCount changes (after the initial mount) the iframe `key`
    // changes and React remounts it. Without this effect the new iframe sits
    // blank because isIframeLoading is still false from the previous load —
    // the user sees a white panel with no progress indicator while the new
    // load is in-flight on slow networks.
    const isInitialRetryMountRef = useRef(true);
    useEffect(() => {
        if (isInitialRetryMountRef.current) {
            isInitialRetryMountRef.current = false;
            return;
        }
        setIsIframeLoading(true);
        setLoadingProgress(0);
        setShowProgressBar(true);
    }, [retryCount]);

    // Reset loading state on a real navigation (version, page, env, host).
    // Listening on source-of-truth props instead of `getIframeUrl` so a retry
    // bumping `retryCount` (which `getIframeUrl` also closes over for /__bust)
    // doesn't loop back through here and reset the retry to 0.
    useEffect(() => {
        if (!stepId) return;
        setIsIframeLoading(true);
        setLoadingProgress(0);
        setShowProgressBar(true);
        setRetryCount(0);
        setLoadFailed(false);
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
        }
    }, [
        stepId,
        currentVersion,
        selectedPageId,
        selectedEnvironment,
        useCustomDomain,
        componentServerUrl,
    ]);

    // Reload from parent is handled declaratively: `reloadTrigger` is part of
    // the iframe's `key` prop, so any change unmounts and remounts the element
    // with a fresh src. No imperative `iframeRef.current.src = …` setter — that
    // pattern races React's prop-driven src and produces the duplicate
    // (canceled) document loads visible in DevTools on slow networks.
    useEffect(() => {
        if (!reloadTrigger || !stepId) return;
        setIsIframeLoading(true);
        setLoadingProgress(0);
        setShowProgressBar(true);
        setRetryCount(0);
        setLoadFailed(false);
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
        }
    }, [reloadTrigger, stepId]);

    // Keep refs in sync so the offline/online listeners (which run with stale
    // closures from `[]` deps) can read the latest loading + retry state.
    useEffect(() => {
        isIframeLoadingRef.current = isIframeLoading;
    }, [isIframeLoading]);
    useEffect(() => {
        retryCountRef.current = retryCount;
    }, [retryCount]);
    useEffect(() => {
        loadFailedRef.current = loadFailed;
    }, [loadFailed]);

    // Seed offlineSinceRef on mount if the app was already offline when the
    // component mounted (no preceding 'offline' event would fire). Without
    // this, the first 'online' event after mounting offline computes a
    // duration of 0 and skips recovery — leaving the iframe stuck on its
    // initial-failed render.
    useEffect(() => {
        if (typeof navigator !== "undefined" && navigator.onLine === false) {
            offlineSinceRef.current = Date.now();
        }
    }, []);

    // Network online/offline coordination with four safeguards:
    //   1. Always park retry-intent on offline. Even if the iframe had already
    //      loaded, going offline likely broke its internal connections —
    //      coming back online should refresh it. This was the bug where
    //      offline → online silently did nothing on a loaded iframe.
    //   2. Brief-flap suppression. If offline lasted < 300ms we treat it as
    //      transient noise (some flaky networks fire spurious offline events)
    //      and don't disrupt a healthy load.
    //   3. Settle delay. After 'online' fires we wait 500ms before firing the
    //      retry — the link often flaps right after recovery, and we don't
    //      want to slam the server with a request that'll just fail again.
    //   4. Re-check on resume. Inside the settle timeout we re-verify
    //      navigator.onLine; if it flipped offline again during the wait, we
    //      re-defer instead of firing a doomed retry.
    useEffect(() => {
        const handleOnline = () => {
            setIsOffline(false);

            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
                retryTimeoutRef.current = null;
            }

            const offlineDuration = offlineSinceRef.current
                ? Date.now() - offlineSinceRef.current
                : 0;
            offlineSinceRef.current = null;

            const hasPending = !!pendingRetryRef.current;
            const wasExhausted = retryCountRef.current >= 3;
            // Real offline (not a brief flap) is itself a recovery trigger,
            // even on a previously-healthy iframe — its in-iframe app sockets
            // and API calls almost certainly failed during the outage.
            const wasMeaningfullyOffline = offlineDuration > 300;
            // If the iframe currently shows the failure state, retry. Covers
            // the "opened the app while offline → load failed → came online"
            // flow, where pendingRetryRef may have been cleared by the
            // progress-completion effect before this event fires.
            const hasFailed = loadFailedRef.current;

            if (
                !hasPending &&
                !wasExhausted &&
                !wasMeaningfullyOffline &&
                !hasFailed
            ) {
                pendingRetryRef.current = null;
                return;
            }

            const reason =
                pendingRetryRef.current?.reason ||
                (hasFailed
                    ? "load failed — online recovery"
                    : wasExhausted
                    ? "exhausted retries — online recovery"
                    : `online recovery (${offlineDuration}ms offline)`);
            pendingRetryRef.current = null;

            retryTimeoutRef.current = setTimeout(() => {
                if (
                    typeof navigator !== "undefined" &&
                    navigator.onLine === false
                ) {
                    pendingRetryRef.current = { reason, at: Date.now() };
                    return;
                }
                console.warn(
                    `[AppPreviewIframe] Resuming after online — ${reason}`
                );
                setLoadFailed(false);
                setRetryCount((prev) => (prev >= 3 ? 1 : prev + 1));
            }, 500);
        };

        const handleOffline = () => {
            setIsOffline(true);
            offlineSinceRef.current = Date.now();
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
                retryTimeoutRef.current = null;
            }
            // Always record intent. The duration check in handleOnline decides
            // whether to act on it — that way a loaded iframe still recovers
            // after a real outage, but brief flaps don't disrupt healthy loads.
            if (!pendingRetryRef.current) {
                pendingRetryRef.current = {
                    reason: "interrupted by offline",
                    at: Date.now(),
                };
            }
        };

        window.addEventListener("online", handleOnline);
        window.addEventListener("offline", handleOffline);
        return () => {
            window.removeEventListener("online", handleOnline);
            window.removeEventListener("offline", handleOffline);
        };
    }, []);

    // Load watchdog: if the iframe stays in loading state for too long without
    // onLoad or onError firing, the TCP connection likely never opened (e.g.
    // ALB routed to a dead target, connection reset, DNS miss). Before tripping
    // the retry, check whether the iframe has sent any postMessage recently —
    // a slow-but-alive load shouldn't be canceled. The base timeout grows with
    // retryCount so flaky networks get more headroom on later attempts.
    useEffect(() => {
        if (loadWatchdogRef.current) {
            clearTimeout(loadWatchdogRef.current);
            loadWatchdogRef.current = null;
        }
        if (isIframeLoading) {
            const baseTimeout = 30000 * Math.pow(2, retryCount); // 30s, 60s, 120s, 240s
            const tick = () => {
                const sinceActivity =
                    Date.now() - lastIframeActivityRef.current;
                // If we heard from the iframe in the last 8s, it's alive —
                // give it another 8s before re-checking.
                if (lastIframeActivityRef.current > 0 && sinceActivity < 8000) {
                    loadWatchdogRef.current = setTimeout(
                        tick,
                        8000 - sinceActivity
                    );
                    return;
                }
                scheduleRetry("load watchdog timeout");
            };
            loadWatchdogRef.current = setTimeout(tick, baseTimeout);
        }
        return () => {
            if (loadWatchdogRef.current) {
                clearTimeout(loadWatchdogRef.current);
                loadWatchdogRef.current = null;
            }
        };
    }, [isIframeLoading, retryCount, scheduleRetry]);

    // Cleanup timeouts and any in-flight probe on unmount
    useEffect(() => {
        return () => {
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
            }
            if (selectModeTimeoutRef.current) {
                clearTimeout(selectModeTimeoutRef.current);
            }
            if (loadWatchdogRef.current) {
                clearTimeout(loadWatchdogRef.current);
            }
            if (probeAbortRef.current) {
                probeAbortRef.current.abort();
                probeAbortRef.current = null;
            }
        };
    }, []);

    // Manage simulated progress
    useEffect(() => {
        let interval;
        if (isIframeLoading) {
            setShowProgressBar(true);
            setLoadingProgress(10); // Start with a small boost

            interval = setInterval(() => {
                setLoadingProgress((prev) => {
                    if (prev >= 90) {
                        clearInterval(interval);
                        return 90;
                    }
                    // Slower progress as it gets closer to 90%
                    const remaining = 95 - prev;
                    const step = Math.max(0.2, remaining / 20);
                    return prev + step;
                });
            }, 100);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [isIframeLoading]);

    // Handle progress completion and bar hiding
    useEffect(() => {
        if (loadingProgress >= 100) {
            setIsIframeLoading(false);
            // Only clear deferred retry intent on a *healthy* finish. If the
            // load gave up (loadFailed), the intent must survive so the next
            // 'online' event resumes — e.g. opened the app while offline,
            // initial load failed, user comes back online and expects retry.
            if (!loadFailed) {
                pendingRetryRef.current = null;
            }
            // Wait a bit before hiding the bar to show 100%
            const timeout = setTimeout(() => {
                setShowProgressBar(false);
                setLoadingProgress(0);
            }, 400);
            return () => clearTimeout(timeout);
        }
    }, [loadingProgress, loadFailed]);

    return (
        <div
            className={`relative h-full ${
                isMobilePreview
                    ? "w-[430px] max-w-full border-x border-gray-200 bg-white shadow-sm"
                    : "w-full"
            }`}
        >
            {showProgressBar && (
                <div
                    className="absolute top-0 left-0 h-1 bg-blue-600 z-20 transition-all duration-300 ease-out shadow-sm"
                    style={{
                        width: `${loadingProgress}%`,
                        opacity: loadingProgress === 100 ? 0 : 1,
                    }}
                />
            )}
            {isSelectMode && (
                <div className="absolute top-0 left-0 right-0 z-20 bg-blue-500 text-white text-xs text-center py-1 font-medium">
                    Click on any element to reference it
                </div>
            )}
            <StatusBanner
                isOffline={isOffline}
                loadFailed={loadFailed}
                isRetrying={!isOffline && retryCount > 0 && isIframeLoading}
                t={t}
            />
            <iframe
                ref={iframeRef}
                title="App Preview"
                allow="accelerometer; autoplay; camera; encrypted-media; fullscreen; geolocation; gyroscope; microphone; midi; clipboard-read; clipboard-write; payment; usb; vr; xr-spatial-tracking; screen-wake-lock; magnetometer; ambient-light-sensor; battery; gamepad; picture-in-picture; display-capture; bluetooth;"
                id="preview-iframe"
                key={`${stepId}-${
                    selectedEnvironment || "prod"
                }-${currentVersion}-${retryCount}-${reloadTrigger || 0}`}
                className={`transition-all duration-300 ease-in-out w-full h-full border-0 ${
                    isSelectMode ? "ring-2 ring-blue-500 ring-inset" : ""
                }`}
                src={getIframeUrl()}
                onLoad={handleIframeLoad}
                onError={handleIframeError}
            />
        </div>
    );
}

// Floating status pill rendered at the top-center of the iframe.
// Surfaces three states without disrupting the iframe content:
//   - offline:   amber dot, pinging halo, "you're offline" copy
//   - retrying:  blue dot, soft pulse, "reconnecting" copy
//   - failed:    red dot, "couldn't load" copy
// Offline takes priority over the other two.
function StatusBanner({ isOffline, loadFailed, isRetrying, t }) {
    const visible = isOffline || loadFailed || isRetrying;
    const tone = isOffline
        ? { dot: "bg-amber-500", halo: "bg-amber-400" }
        : loadFailed
        ? { dot: "bg-red-500", halo: "bg-red-400" }
        : { dot: "bg-blue-500", halo: "bg-blue-400" };
    const label = isOffline
        ? t("appPreview.offlineNotice") || "You're offline — we'll retry when you're back"
        : loadFailed
        ? t("appPreview.loadFailedNotice") || "Couldn't load the preview"
        : t("appPreview.retryingNotice") || "Reconnecting…";

    return (
        <div
            aria-live="polite"
            className={`pointer-events-none absolute top-3 left-1/2 -translate-x-1/2 z-30 transition-all duration-300 ease-out ${
                visible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 -translate-y-2"
            }`}
        >
            <div className="flex items-center gap-2.5 rounded-full border border-gray-200/60 bg-white/90 px-4 py-1.5 text-sm font-medium text-gray-800 shadow-lg backdrop-blur-md">
                <span className="relative flex h-2 w-2">
                    <span
                        className={`absolute inline-flex h-full w-full rounded-full opacity-75 ${tone.halo} ${
                            isOffline || isRetrying ? "animate-ping" : ""
                        }`}
                    />
                    <span
                        className={`relative inline-flex h-2 w-2 rounded-full ${tone.dot}`}
                    />
                </span>
                <span>{label}</span>
            </div>
        </div>
    );
}
